// sentryConfig.ts
import * as Sentry from '@sentry/react';
import React from 'react';
import {
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
} from 'react-router-dom';

const getEnvironment = (): string => {
  // Use build-time environment variable first (most reliable)
  if (import.meta.env.VITE_SENTRY_ENVIRONMENT) {
    return import.meta.env.VITE_SENTRY_ENVIRONMENT;
  }

  // Fallback to hostname detection for local development
  if (window.location.hostname.includes('localhost')) {
    return 'local';
  } else if (window.location.hostname.includes('dashboard-stg')) {
    return 'stg';
  } else if (window.location.hostname.includes('dashboard')) {
    return 'prod';
  }
  return 'unknown';
};

const getRelease = (): string | undefined => {
  // Use build-time release variable (commit SHA from CI)
  if (import.meta.env.VITE_SENTRY_RELEASE) {
    return import.meta.env.VITE_SENTRY_RELEASE;
  }

  return undefined;
};

export const initSentry = () => {
  const environment = getEnvironment();
  const release = getRelease();

  // Only initialize Sentry if the environment is not 'local'
  if (environment !== 'local') {
    Sentry.init({
      dsn: 'https://67c7aa667e5b8c9e6358a15c2d945343@sentry.tryboxy.dev/4',
      environment,
      release,
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.browserProfilingIntegration(),
        Sentry.replayIntegration({
          maskAllText: false,
          maskAllInputs: false,
        }),
        Sentry.reactRouterV6BrowserTracingIntegration({
          useEffect: React.useEffect,
          useLocation,
          useNavigationType,
          createRoutesFromChildren,
          matchRoutes,
        }),
      ],
      replaysSessionSampleRate: environment === 'prod' ? 0.2 : 1.0,
      replaysOnErrorSampleRate: environment === 'prod' ? 1.0 : 0.3,
      tracesSampleRate: environment === 'prod' ? 0.2 : 1.0,
      profilesSampleRate: environment === 'prod' ? 0.1 : 1.0,
      tracePropagationTargets: ['localhost', 'dashboard-stg.tryboxy.dev', 'dashboard.tryboxy.com'],

      // Customize error fingerprinting for better grouping
      beforeSend(event, hint) {
        const error = hint.originalException;

        // Handle Axios errors with better grouping
        if (error && typeof error === 'object' && 'isAxiosError' in error) {
          const axiosError = error as any;
          const url = axiosError.config?.url || 'unknown';
          const method = axiosError.config?.method?.toUpperCase() || 'UNKNOWN';
          const status = axiosError.response?.status || 'network';

          // Group by endpoint + status code instead of generic AxiosError
          event.fingerprint = [`axios-${method}-${url}-${status}`];

          // Add custom tags for filtering
          event.tags = {
            ...event.tags,
            api_endpoint: url,
            http_method: method,
            http_status: status.toString(),
            error_type:
              status >= 500 ? 'server_error' : status >= 400 ? 'client_error' : 'network_error',
          };

          // Improve error message
          const errorMessage = axiosError.response?.data?.message || axiosError.message;
          event.message = `API Error: ${method} ${url} - ${status} ${errorMessage}`;
        }

        // Sanitize sensitive data
        if (event.request?.headers) {
          delete event.request.headers['Authorization'];
          delete event.request.headers['authorization'];
        }

        return event;
      },

      // Filter breadcrumbs to reduce noise
      beforeBreadcrumb(breadcrumb) {
        // Skip console breadcrumbs in production
        if (environment === 'prod' && breadcrumb.category === 'console') {
          return null;
        }
        return breadcrumb;
      },
    });
  }
};
